import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useSendBudgetSignature } from "@/hooks/useBudgetSignatures";
import type { Budget } from "@/hooks/useBudgets";
import { ClientSignersEditor } from "./documents/ClientSignersEditor";
import type { ClientSigner } from "./documents/types";

interface Props {
  budget: Budget | null;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}

export function BudgetSignatureDialog({ budget, open, onOpenChange }: Props) {
  const [signers, setSigners] = useState<ClientSigner[]>([]);
  const [flow, setFlow] = useState<"parallel" | "sequential">("parallel");
  const send = useSendBudgetSignature();

  useEffect(() => {
    if (open && budget?.lead) {
      // Reuse whatever the operator already set up while placing pdf_upload
      // signature fields (same order the fields were assigned to), so the
      // signatures we create line up with those fields. Falls back to the
      // lead when the document doesn't need positioned fields (auto/richtext)
      // or hasn't been configured yet.
      setSigners(
        budget.pending_client_signers?.length
          ? budget.pending_client_signers
          : [{ name: budget.lead.name, email: budget.lead.email ?? "" }],
      );
      setFlow("parallel");
    }
  }, [open, budget]);

  if (!budget) return null;

  const handleSend = async () => {
    const valid = signers.every((s) => s.name.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.email));
    if (!valid || signers.length === 0) {
      toast.error("Preencha nome e e-mail válidos de todos os signatários.");
      return;
    }
    try {
      await send.mutateAsync({
        budgetId: budget.id,
        flow,
        signers,
      });
      onOpenChange(false);
    } catch {
      // useSendBudgetSignature already toasts the error; keep the dialog open to retry.
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Enviar para assinatura digital</DialogTitle>
          <DialogDescription>
            Cada signatário receberá um e-mail com um link único (válido por 30 dias) para revisar e
            assinar a proposta.
            {budget.status !== "draft" &&
              " Reenviar descarta as assinaturas anteriores e gera novos links."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <ClientSignersEditor value={signers} onChange={setSigners} />

          {signers.length > 1 && (
            <div className="space-y-2">
              <span id="signing-flow-label" className="text-sm font-medium leading-none">
                Ordem de assinatura
              </span>
              <RadioGroup
                aria-labelledby="signing-flow-label"
                value={flow}
                onValueChange={(v) => setFlow(v as "parallel" | "sequential")}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="parallel" id="parallel" />
                  <Label htmlFor="parallel" className="cursor-pointer font-normal">
                    Qualquer ordem (todos recebem ao mesmo tempo)
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="sequential" id="sequential" />
                  <Label htmlFor="sequential" className="cursor-pointer font-normal">
                    Sequencial (próximo só recebe após o anterior assinar)
                  </Label>
                </div>
              </RadioGroup>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSend} disabled={send.isPending}>
            {send.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Send className="mr-2 h-4 w-4" />
            )}
            Enviar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
